import { useApi } from '@/contexts/ApiContext'

// Helper to unwrap Axios responses (data field) and gracefully handle undefined
const unwrap = async <T = any>(promise: Promise<any>): Promise<T> => {
  const res = await promise
  // openapi-generator may return either AxiosResponse or plain value
  return (res && res.data) ?? res
}

/**
 * Adapter between legacy Team components and the new generated TeamApi.
 * Only the subset of methods currently used by the UI is implemented.
 * Missing backend endpoints are stubbed to return safe defaults to keep the UI functional.
 */
export function useTeamService() {
  const { TeamApi } = useApi()

  /* -----------------------------  READ OPERATIONS ---------------------------- */
  const getTeamMembers = async (teamId: string) => {
    // Backend пока не предоставляет отдельную точку – возвращаем пустой массив
    return []
  }

  const getTeamInvitations = (teamId: string) => unwrap(TeamApi.listTeamInvitations(teamId))
  const getTeamAuditLogs = (teamId: string) => unwrap(TeamApi.getTeamAuditLogs(teamId))

  const getTeamSecuritySettings = (teamId: string) => unwrap(TeamApi.getTeamSecuritySettings(teamId))
  const getTeamUISettings = (teamId: string) => unwrap(TeamApi.getTeamUISettings(teamId))
  const getTeamNotificationSettings = (teamId: string) => {
    // Using generic platform "web" until we support multiple platforms
    // getTeamNotificationSettingsV2(teamId, platformId)
    if ((TeamApi as any).getTeamNotificationSettingsV2) {
      // @ts-ignore next-line
      return unwrap(TeamApi.getTeamNotificationSettingsV2(teamId, 'web'))
    }
    return Promise.resolve(null)
  }
  const getTeamUserSettings = (teamId: string) => unwrap(TeamApi.getTeamUserSettings(teamId))

  const getTeamMembershipRequests = (teamId: string) => unwrap(TeamApi.listTeamMembershipRequests(teamId, ''))
  const getTeamRoleRequests = (teamId: string) => unwrap(TeamApi.listTeamRoleRequests(teamId, ''))
  const getTeamOwnershipRequests = (teamId: string) => unwrap(TeamApi.listTeamOwnershipRequests(teamId))

  const getTeamContacts = async (_teamId: string) => {
    // Endpoint отсутствует → временно возвращаем пустой массив
    return []
  }

  const getTeamRoles = async (_teamId: string) => {
    // Endpoint пока отсутствует
    return []
  }

  /* ----------------------------- WRITE / ACTIONS ---------------------------- */
  const inviteUserToTeam = (teamId: string, payload: any) => unwrap(TeamApi.inviteUserToTeam(teamId, payload))
  const cancelTeamInvitation = (teamId: string, invitationId: string) => unwrap((TeamApi as any).cancelTeamInvitationRequest?.(teamId, invitationId, '') ?? Promise.resolve(null))

  const updateTeam = (_teamId: string, _payload: any) => {
    // Endpoint ещё не реализован
    return Promise.resolve(null)
  }

  /* -----------------------------  ROLE MANAGEMENT --------------------------- */
  const getTeamAvailableActions = (teamId: string, language = 'en') => unwrap(TeamApi.getTeamAvailableActions(teamId, language))
  const createRole = (teamId: string, payload: any) => unwrap(TeamApi.createRole(teamId, payload))
  const updateRole = (_teamId: string, _roleId: string, _payload: any) => Promise.resolve(null)
  const deleteRole = (_teamId: string, _roleId: string) => Promise.resolve(null)

  const updateTeamSecuritySettings = (teamId: string, settings: any) => {
    return Promise.resolve(settings)
  }
  const updateTeamUISettings = (teamId: string, settings: any) => {
    return Promise.resolve(settings)
  }
  const updateTeamNotificationSettings = (teamId: string, settings: any) => {
    return Promise.resolve(settings)
  }
  const updateTeamUserSettings = (teamId: string, settings: any) => {
    return Promise.resolve(settings)
  }

  return {
    /* reads */
    getTeamMembers,
    getTeamInvitations,
    getTeamAuditLogs,
    getTeamSecuritySettings,
    getTeamUISettings,
    getTeamNotificationSettings,
    getTeamUserSettings,
    getTeamMembershipRequests,
    getTeamRoleRequests,
    getTeamOwnershipRequests,
    getTeamContacts,
    getTeamRoles,

    /* writes/actions */
    inviteUserToTeam,
    cancelTeamInvitation,
    updateTeam,

    /* role management */
    getTeamAvailableActions,
    createRole,
    updateRole,
    deleteRole,

    /* settings updates */
    updateTeamSecuritySettings,
    updateTeamUISettings,
    updateTeamNotificationSettings,
    updateTeamUserSettings,
  }
} 